import { ThemeSchemaResponse } from '@api';
import { TabLines } from '@design/components/tab-lines/TabLines';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { ThemeHeader } from '../../../modules/reflexivity-graph/components/theme-header/ThemeHeader';
import styles from './ThemeOverviewHeader.module.scss';

export type ThemeOverviewHeaderProps = {
  theme: ThemeSchemaResponse;
};

export const THEME_OVERVIEW_CONTENT_ID = 'theme-overview-content';
export const THEME_TAB_COMPANIES = 'companies';

export const ThemeOverviewHeader = ({ theme }: ThemeOverviewHeaderProps) => {
  const { t } = useTranslation('widget');
  const { params, setQueryParams } = useQueryParams();
  const isMacroFinancialThemeEnabled = useFlags(state =>
    state.getFeatureFlagState('KG-786-Macro_Financial_Theme')
  );
  const isThemeEarningsEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4744-theme-earnings')
  );
  const activeTab = params[queryKeys.activeSubTab] || 'overview';

  if (!theme) {
    throw new Error('ThemeOverviewHeader: No theme found');
  }

  const isMacroFinancialTheme =
    isMacroFinancialThemeEnabled &&
    (theme.type === 'financial' || theme.type === 'macro');
  const isMSTheme = !!theme.metadata?.ms_basket_ticker;

  const overviewTab = {
    tabKey: 'overview',
    tabLabel: t('widget:themeOverview.overview'),
  };
  const companiesTab = {
    tabKey: THEME_TAB_COMPANIES,
    tabLabel: t('widget:themeOverview.companies'),
  };
  const performanceTab = {
    tabKey: 'performance',
    tabLabel: t('widget:themeOverview.performance'),
  };
  const earningsTab = {
    tabKey: 'earnings',
    tabLabel: t('widget:themeOverview.earnings'),
  };

  const tabs = (
    isMacroFinancialTheme
      ? [overviewTab, companiesTab]
      : isThemeEarningsEnabled
        ? [overviewTab, earningsTab, companiesTab, performanceTab]
        : [overviewTab, companiesTab, performanceTab, earningsTab]
  ).filter(tab => !(isMSTheme && tab.tabKey === 'performance'));

  return (
    <div className={styles.themeHeaderRoot} data-testid="theme-overview-header">
      <div
        className={clsx(styles.innerContainer, {
          [styles.macroFinancialTheme]: isMacroFinancialTheme,
        })}
      >
        <ThemeHeader theme={theme} />
        <TabLines
          tabs={tabs}
          activeTab={activeTab}
          onChange={idx =>
            setQueryParams({ [queryKeys.activeSubTab]: tabs[idx].tabKey })
          }
        />
      </div>
    </div>
  );
};
